import React from 'react';
import CoinMark, { COIN_COLORS } from './CoinMark';

/**
 * The picture at the top of a completed purchase.
 *
 * Buying points is the one moment in this product that is a REWARD rather than
 * a transaction, and it used to be announced by a green bar with a sentence in
 * it — the same treatment as "your settings were saved". A coin bursting open
 * is what the moment actually is, and it costs nothing: inline SVG and the
 * keyframes in `animations.css`, no dependency, inside a modal that is already
 * carrying a Stripe iframe.
 *
 * ⚠️ IT CARRIES NO INFORMATION, DELIBERATELY. Everything the panel means is in
 * the text underneath, which is why `aria-hidden` is correct here and why the
 * reduced-motion rule can delete the moving parts outright without leaving a
 * reader short of anything.
 *
 * A PLAN GETS A DIFFERENT MARK. Coins are what a point pack pays out; a
 * subscription pays out access, so it gets a rosette instead. Showing coins for
 * a plan would picture something the buyer did not receive.
 */

interface PurchaseCelebrationProps {
  variant?: 'points' | 'plan';
}

/** Where each coin flies, in order. Hand-placed rather than evenly spaced —
 *  a perfect ring reads as a loading spinner, not a burst. */
const COINS = [
  { x: -60, y: -36, spin: -160, delay: 40, size: 19 },
  { x: 56, y: -42, spin: 190, delay: 0, size: 22 },
  { x: -76, y: 8, spin: 120, delay: 130, size: 16 },
  { x: 74, y: 12, spin: -140, delay: 90, size: 18 },
  { x: -42, y: 42, spin: 200, delay: 180, size: 15 },
  { x: 46, y: 46, spin: -180, delay: 150, size: 17 },
  { x: 4, y: -58, spin: 150, delay: 70, size: 16 },
  { x: -20, y: 56, spin: -120, delay: 210, size: 14 },
];

/** Twinkles, kept off the coin's own face so they read as background. */
const SPARKLES = [
  { x: -62, y: -22, size: 10, delay: 200 },
  { x: 60, y: -8, size: 12, delay: 700 },
  { x: -34, y: 34, size: 8, delay: 1100 },
  { x: 40, y: 30, size: 9, delay: 450 },
  { x: 0, y: -46, size: 7, delay: 900 },
];

const Sparkle: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    {/* A four-point star, not a five-point one: it reads as a glint rather
        than as a rating. */}
    <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
  </svg>
);

const PurchaseCelebration: React.FC<PurchaseCelebrationProps> = ({ variant = 'points' }) => {
  const isPlan = variant === 'plan';

  // From the shared table, so the burst and every other coin in the product
  // are the same object — see CoinMark.
  const { face, light: faceLight } = COIN_COLORS;

  return (
    <div className="celebration" aria-hidden="true">
      <div className="celebration-ring" style={{ color: faceLight }} />

      {!isPlan &&
        COINS.map((coin, i) => (
          <span
            key={i}
            className="celebration-coin"
            style={
              {
                '--fly-x': `${coin.x}px`,
                '--fly-y': `${coin.y}px`,
                '--fly-spin': `${coin.spin}deg`,
                animationDelay: `${coin.delay}ms`,
              } as React.CSSProperties
            }
          >
            {/* Rim, face and a highlight — three tones, because two flat
                circles at this size read as a dot rather than as metal. */}
            <svg width={coin.size} height={coin.size} viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="11" fill={faceLight} />
              <circle cx="12" cy="12" r="8" fill={face} />
              <circle cx="9" cy="9" r="2.6" fill="#FFFFFF" opacity="0.55" />
            </svg>
          </span>
        ))}

      {SPARKLES.map((s, i) => (
        <span
          key={i}
          className="celebration-sparkle"
          style={{
            left: `calc(50% + ${s.x}px)`,
            top: `calc(50% + ${s.y}px)`,
            animationDelay: `${s.delay}ms`,
          }}
        >
          <Sparkle size={s.size} color={faceLight} />
        </span>
      ))}

      <div className="celebration-disc">
        <div className="celebration-disc-float">
          {/* The same coin the prompt shows, at the size this panel wants. The
              shine belongs here and only here: on a small icon it is noise. */}
          <CoinMark size={76} variant={variant} shine />
        </div>
      </div>
    </div>
  );
};

export default PurchaseCelebration;
